import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileCard, { cardBackground } from '../components/ProfileCard';
import { useAuth } from '../AuthContext';
import { useRealtime, useRealtimeContext } from '../RealtimeContext';
import ReportDialog from '../dm/ReportDialog';
import { BlockDialog } from '../dm/ConfirmDialogs';
import GroupMembers from '../dm/GroupMembers';
import { dmApi, displayName } from '../dm/format';
import { ContextMenu, MenuItem, Icons } from '../dm/Menu';

const OWN_STATUS = { online: 'online', away: 'away', dnd: 'dnd', invisible: 'offline' };

// Right column in a chat: profile card of the chat partner (block and report in its "more" menu),
// or the member list of a group.
// conversationId for an existing chat, userId for a draft chat (/dm/user/:userId).
export default function MemberPanel({ conversationId, userId, onClose }) {
  const navigate = useNavigate();
  const { user, nicknames } = useAuth();
  const { presence } = useRealtimeContext();
  const [profile, setProfile] = useState(null);
  const [group, setGroup] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [menu, setMenu] = useState(null); // { x, y }
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // 'block' | 'report'

  const load = useCallback(async () => {
    try {
      const res = conversationId
        ? await dmApi.get(`/api/dm/conversations/${conversationId}`)
        : await dmApi.get(`/api/dm/with/${userId}`);
      if (res.data.type === 'group') {
        setGroup(res.data);
        return;
      }
      const partner = res.data.partner;
      if (!partner) return;
      const full = await dmApi.get(`/api/users/${partner.username}`);
      setProfile(full.data);
      setBlocked(!!res.data.blocked_by_me);
    } catch {}
  }, [conversationId, userId]);

  useEffect(() => { setProfile(null); setGroup(null); load(); }, [load]);
  useRealtime('dm:block', (e) => { if (profile && e.userId === profile.id) setBlocked(e.blocked); });
  useRealtime('dm:conversation_updated', (e) => { if (group && e.conversation_id === group.id) load(); });

  const toggleBlock = async () => {
    setBusy(true);
    try {
      if (blocked) await dmApi.delete(`/api/users/${profile.id}/block`);
      else await dmApi.post(`/api/users/${profile.id}/block`);
      setBlocked(!blocked);
    } catch {}
    setBusy(false);
    setDialog(null);
  };

  const name = profile ? displayName(profile, nicknames) : '';

  const closeButton = onClose && (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="absolute right-2 top-[calc(8px_+_var(--safe-top))] flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 text-white"
      style={{ background: 'rgba(0,0,0,0.4)' }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>
  );

  return (
    <aside
      className="flex h-full w-panel max-w-[85vw] shrink-0 flex-col overflow-y-auto overscroll-contain bg-surface pb-[var(--safe-bottom)]"
      style={{ background: cardBackground(profile) }}
    >
      {group ? (
        <GroupMembers
          group={group}
          me={user?.id}
          myStatus={OWN_STATUS[user?.status_preference] || 'online'}
          nicknames={nicknames}
          presence={presence}
          closeButton={closeButton}
          onChanged={load}
        />
      ) : !profile ? (
        <>
          <div className="relative shrink-0 bg-brand" style={{ height: 'calc(96px + var(--safe-top))' }}>{closeButton}</div>
          <div className="flex justify-center py-10">
            <div className="h-6 w-6 animate-spin rounded-full border-[3px] border-solid border-border border-t-purple" />
          </div>
        </>
      ) : (
        <ProfileCard
          profile={profile}
          nickname={nicknames[profile.id]}
          status={blocked ? 'offline' : (presence[profile.id] || 'offline')}
          bannerTopInset
          topRight={closeButton}
          nameAction={(
            <button
              type="button"
              onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setMenu({ x: r.right - 160, y: r.bottom + 6 }); }}
              aria-label={`More options for ${name}`}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-text-muted hover:bg-surface-3 hover:text-text"
            >
              {Icons.more}
            </button>
          )}
        >
          <button
            type="button"
            onClick={() => navigate(`/profile/${profile.username}`)}
            className="mt-4 w-full cursor-pointer rounded-sm border-0 bg-surface-3 py-2 font-display text-sm font-bold text-text hover:bg-border"
          >
            View full profile
          </button>
        </ProfileCard>
      )}

      {menu && profile && (
        <ContextMenu x={menu.x} y={menu.y} width={160} onClose={() => setMenu(null)}>
          <MenuItem icon={Icons.block} danger={!blocked} disabled={busy} onClick={() => { setMenu(null); if (blocked) toggleBlock(); else setDialog('block'); }}>
            {blocked ? 'Unblock' : 'Block'}
          </MenuItem>
          <MenuItem icon={Icons.flag} danger onClick={() => { setMenu(null); setDialog('report'); }}>
            Report
          </MenuItem>
        </ContextMenu>
      )}
      {dialog === 'report' && profile && (
        <ReportDialog userId={profile.id} name={name} onClose={() => setDialog(null)} />
      )}
      {dialog === 'block' && profile && (
        <BlockDialog name={name} busy={busy} onConfirm={toggleBlock} onClose={() => setDialog(null)} />
      )}
    </aside>
  );
}
